import { InputAdornment } from "@material-ui/core";
import * as React from "react";
import { TextInput, TextInputProps } from "./textInput";

export type NumericInputProps = {
    id: string;
    allowDecimal?: boolean;
    startAdornment?: "dollar" | JSX.Element;
    endAdornment?: "percent" | JSX.Element;
} & Omit<
    TextInputProps,
    | "hideCharacterLimit"
    | "suppressContentEditableWarning"
    | "startAdornment"
    | "endAdornment"
>;

export const NumericInput = (props: NumericInputProps) => {
    const {
        startAdornment: startAdornmentProp,
        endAdornment: endAdornmentProp,
        allowDecimal = false,
        onChange,
        ...passthrough
    } = props;

    const regEx = allowDecimal ? /^\d*\.?\d{0,2}$/ : /^\d*$/;

    const startAdornment: JSX.Element = React.useMemo(() => {
        if (!startAdornmentProp) return null;
        if (startAdornmentProp === "dollar") {
            return <InputAdornment position="start">$</InputAdornment>;
        } else {
            return startAdornmentProp;
        }
    }, [startAdornmentProp]);

    const endAdornment: JSX.Element = React.useMemo(() => {
        if (!endAdornmentProp) return null;
        if (endAdornmentProp === "percent") {
            return <InputAdornment position="end">%</InputAdornment>;
        } else {
            return endAdornmentProp;
        }
    }, [endAdornmentProp]);

    const handleChange = (e: React.ChangeEvent<any>) => {
        let { value } = e.target;

        // do not allow values greater than 20 million.  This breaks things on the back end.
        if (!isNaN(+value) && +value > 20000000) return;

        if (value.length > 1 && value.startsWith("0")) {
            value = value.substr(1, value.length);
        }

        e.target.value = value.toString();
        if (value === "" || regEx.test(value)) {
            onChange?.(e);
        }
    };

    const handleBlur = (e: React.FocusEvent<any>) => {
        const { value } = e.target;
        if (allowDecimal) {
            e.target.value = (+value).toFixed(2);
            onChange?.(e);
        }
        props.onBlur?.(e);
    };

    return (
        <TextInput
            {...passthrough}
            variant="standard"
            onBlur={handleBlur}
            onChange={handleChange}
            startAdornment={startAdornment}
            endAdornment={endAdornment}
            type="tel"
        />
    );
};
